import { useEffect, useState } from "react";
import { SwapIcon } from "../../icons";
import GuideHint from "../GetStartedGuide/GuideHint";
import { GetDepositAddressQRCode } from "../../../redux/actions/CryptoTransaction/GetDepositAddress";
import Skeleton from "../../../components/Common/Skeleton";
import { useDispatch } from "react-redux";


const IconActionButton = ({ children, onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="flex size-9 shrink-0 items-center justify-center rounded-[8px] border border-divider bg-background-surface text-text-secondary custom-transition hover:bg-option-hover hover:text-text-primary active:scale-90 transition-all duration-150 ease-out"
  >
    {children}
  </button>
);



const DepositQrCode = ({ QRcodeData, asset, isloading }) => {
  
  if (isloading) {
    return (
      <div className="text-text-primary flex flex-col items-center justify-center">
        <Skeleton className="rounded w-[200px]  h-[200px]" />
      </div>
    )
  }
  
  return (
    <div className="relative flex size-[200px] shrink-0 items-center justify-center">
      <img
        // src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(address)}`}
        src={`data:image/png;base64,${QRcodeData}`}
        alt="Deposit QR code"
        className="size-full rounded-[4px]"
      />
      <div
        className="absolute flex size-10 items-center justify-center overflow-hidden rounded-full ring-4 ring-white"
        style={
          asset?.iconBg
            ? { backgroundColor: asset.iconBg }
            : { backgroundColor: "#fff" }
        }
      >
        {asset?.logo ? (
          <img
            src={asset.logo}
            alt={asset.currency_code ?? "Token"}
            className="size-10 rounded-full object-cover"
          />
        ) : (
          <span className="text-xs font-semibold text-text-primary">
            {asset?.currency_code}
          </span>
        )}
      </div>
    </div>
  );
}

const CopyIcon = () => (
  <svg className="w-5 h-5 text-text-secondary hover:text-text-primary transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-5 h-5 text-[#4ab933] animate-in zoom-in-75 duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const DepositAddressStep = ({
  selectedAsset,
  selectedNetwork,
  depositAddress,
  onNetworkSwitch,
  onSaveShare,
  spotlightActive = false,
  guideHint = null,
}) => {
  const dispatch = useDispatch();

  const [QRcodeData, setQRcodeData] = useState(null);
  const [isloading, setIsLoading] = useState(false);
  const copyAaddress = `${depositAddress?.crypto_address}${depositAddress?.destination_tag ? `?dt=${depositAddress?.destination_tag}` : ''}`;
  const cryptoAddress = `${depositAddress?.crypto_address}`;
  const cryptoDestinationTag = depositAddress?.destination_tag;

  const [copiedAddress, setCopiedAddress] = useState(false);
  const [copiedTag, setCopiedTag] = useState(false);

  const handleCopy = (text, type) => {
    navigator.clipboard.writeText(text);

    if (type === "address") {
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
    } else {
      setCopiedTag(true);
      setTimeout(() => setCopiedTag(false), 2000);
    }
  };


  const saveShareButton = (
    <button
      type="button"
      onClick={onSaveShare}
      className="flex h-12 w-full shrink-0 items-center justify-center rounded-[10px] bg-[#4ab933] text-sm font-medium leading-[21px] text-white custom-transition hover:bg-[#3fa82c]"
    >
      {/* Save &amp; Share Address */}
      Close
    </button>
  );

  useEffect(() => {
    const fetchQRCode = async () => {
      setIsLoading(true);
      const data = {
        size: 250,
        wallet_address: copyAaddress ?? "",
        logo: selectedAsset.logo || null,
      };

      const res = await dispatch(GetDepositAddressQRCode(data));
      if (res.success) {
        setQRcodeData(res.qr_code);
        setIsLoading(false);
      } else {
        setQRcodeData("");
      }
    };

    if (depositAddress && copyAaddress) {
      fetchQRCode();
    }
  }, [depositAddress, dispatch, selectedAsset?.logo, copyAaddress]);

  const addressSection = (
    <div
      className={`flex min-h-0 flex-col gap-4 overflow-y-auto pr-2 custom-scrollbar ${
        spotlightActive
          ? "max-h-[min(360px,calc(100dvh-18rem))] sm:min-h-0 sm:max-h-none sm:flex-1"
          : "flex-1"
      }`}
    >
      <div className="flex justify-center py-2">
        <DepositQrCode 
          QRcodeData={QRcodeData}
          asset={selectedAsset}
          isloading={isloading}
        />
      </div>

      <div className="flex flex-col gap-4 rounded-[12px] bg-background-surface">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-normal leading-[16.5px] text-text-secondary">
              Asset
            </p>
            <p className="mt-1 text-sm font-semibold leading-[19.5px] text-text-primary">
              {selectedAsset?.currency_code} 
            </p>
            <p className="mt-0.5 text-xs font-normal leading-[16.5px] text-text-secondary">
              {depositAddress?.network}
            </p>
            
          </div>
          <IconActionButton label="Switch network" onClick={onNetworkSwitch}>
            <SwapIcon />
          </IconActionButton>
        </div>

        <div className="border-t border-divider" />

        <div className="flex flex-col space-y-4">
          {/* Deposit Address Row */}
          {cryptoAddress && (
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-normal leading-[16.5px] text-text-secondary">
                  Deposit Address
                </p>
                <p className="mt-1 break-all text-sm font-semibold leading-[19.5px] text-text-primary">
                  {cryptoAddress.slice(0, 6)}
                  <span className="font-normal">{cryptoAddress.slice(6, -6)}</span>
                  {cryptoAddress.slice(-6)}
                </p>
              </div>

              <IconActionButton label="Copy Address" onClick={() => handleCopy(cryptoAddress, "address")}>
                {copiedAddress ? <CheckIcon /> : <CopyIcon />}
              </IconActionButton>
            </div>
          )}

          {/* Destination Tag Row */}
          {cryptoDestinationTag && (
            <div className="flex items-center justify-between gap-3 pt-3 border-t border-divider">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-normal leading-[16.5px] text-text-secondary">
                  Destination tag
                </p>
                <p className="mt-1 break-all text-sm font-semibold leading-[19.5px] text-text-primary">
                  {cryptoDestinationTag}
                </p>
              </div>

              <IconActionButton label="Copy destination tag" onClick={() => handleCopy(cryptoDestinationTag, "tag")}>
                {copiedTag ? <CheckIcon /> : <CopyIcon />}
              </IconActionButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`flex flex-col gap-4 px-4 pt-4 sm:h-full sm:min-h-0 ${
        spotlightActive ? "max-sm:shrink-0 max-sm:pb-4" : "h-full min-h-0"
      }`}
    >
      <h3 className="shrink-0 px-2 text-lg font-semibold leading-7 text-text-primary">
        Deposit Address
      </h3>

      {spotlightActive ? (
        <div className="relative z-40 max-sm:shrink-0 max-sm:-mx-4 -mt-4 max-sm:rounded-[12px] max-sm:bg-background-surface sm:flex sm:min-h-0 sm:flex-1 sm:flex-col sm:rounded-none sm:bg-transparent sm:p-0">
          {guideHint && (
            <div className="pointer-events-none absolute inset-x-4 bottom-full z-50 mb-2 sm:hidden">
              <div className="pointer-events-auto mx-auto w-[90%] max-w-[280px]">
                <GuideHint
                  title={guideHint.title}
                  description={guideHint.description}
                  primaryAction={guideHint.primaryAction}
                  secondaryAction={guideHint.secondaryAction}
                  showPointer={guideHint.showPointer}
                  position={guideHint.position}
                  onSkip={guideHint.onSkip}
                  onDone={guideHint.onDone}
                  onBuyCrypto={guideHint.onBuyCrypto}
                />
              </div>
            </div>
          )}
          <div className="flex min-h-0 flex-col gap-4 max-sm:px-4 max-sm:pb-6 max-sm:pt-4 sm:flex-1">
            {addressSection}
            <div className="shrink-0">{saveShareButton}</div>
          </div>
        </div>
      ) : (
        <>
          {addressSection}
          <div className="mb-2 shrink-0">{saveShareButton}</div>
        </>
      )}
    </div>
  );
};

export default DepositAddressStep;
